import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { GitBranch, FileInput, Search, ClipboardCheck, HelpCircle, CheckCircle2, AlertOctagon, ArrowDown, ExternalLink, Mail, UserCheck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

function Node({ icon: Icon, title, sub, color = "mlily", i }) {
  const map = {
    mlily: "border-mlily-blue/30 bg-blue-50 text-mlily-blue",
    action: "border-amber-300 bg-amber-50 text-amber-700",
    success: "border-emerald-300 bg-emerald-50 text-emerald-700",
    escalate: "border-red-300 bg-red-50 text-red-700",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
      className={cn("rounded-xl border-2 px-5 py-4 w-full max-w-md text-center", map[color])}>
      <div className="flex items-center justify-center gap-2 font-heading font-bold">
        {Icon && <Icon className="w-5 h-5" />} {title}
      </div>
      {sub && <p className="text-xs mt-1 opacity-80">{sub}</p>}
    </motion.div>
  );
}

const Arrow = () => <ArrowDown className="w-6 h-6 text-slate-300 my-1" />;

export default function Workflow() {
  const [settings, setSettings] = useState({});
  const [approvals, setApprovals] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/content/approvals").then((r) => setApprovals(r.data)).catch(() => {});
  }, []);

  const INTAKE = [
    "Ask customer to complete the warranty claim form.",
    "Confirm customer name, contact number, email, retailer, proof of purchase, product model, and issue description.",
    "Request photos and videos where applicable.",
    "Review whether troubleshooting can be attempted first.",
    "Do not promise replacement parts or products before approval.",
    "Record all notes and next actions.",
  ];

  return (
    <div className="space-y-6" data-testid="workflow-page">
      <PageHeader overline="Process" title="Warranty Claim Workflow" icon={GitBranch}
        desc="The end-to-end warranty process. Every case begins with the online claim form." />

      <a href={settings.warranty_claim_form_url || "https://www.mlily.com.au/warranty-claim"} target="_blank" rel="noopener noreferrer"
        data-testid="claim-form-link"
        className="flex items-center justify-between gap-3 rounded-xl border-2 border-mlily-blue bg-mlily-blue text-white px-5 py-4 hover:bg-mlily-bluehover transition-colors max-w-md mx-auto">
        <div className="flex items-center gap-3">
          <FileInput className="w-6 h-6" />
          <div className="text-left">
            <div className="font-heading font-bold">Step 1 · Online Warranty Claim Form</div>
            <div className="text-xs text-blue-100">Customer must complete this first</div>
          </div>
        </div>
        <ExternalLink className="w-5 h-5" />
      </a>

      {/* Intake checklist + approvals */}
      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-6" data-testid="intake-checklist">
          <div className="flex items-center gap-2 mb-4 font-heading font-bold text-slate-900">
            <ClipboardCheck className="w-5 h-5 text-mlily-blue" /> Warranty Claim Intake Checklist
          </div>
          <ul className="space-y-2">
            {INTAKE.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-700">
                <span className="w-5 h-5 rounded border border-slate-300 shrink-0 mt-0.5" />{t}
              </li>
            ))}
          </ul>
          <Link to="/templates" className="inline-flex items-center gap-1 text-sm font-medium text-mlily-blue mt-4" data-testid="workflow-templates-link">
            <Mail className="w-4 h-4" /> Use the customer reply templates →
          </Link>
        </Card>

        <Card className="p-6" data-testid="workflow-approvals">
          <div className="flex items-center gap-2 mb-4 font-heading font-bold text-slate-900">
            <UserCheck className="w-5 h-5 text-mlily-blue" /> Approval Rules
          </div>
          <div className="divide-y divide-slate-100">
            {approvals.map((a) => (
              <div key={a.id} className="py-3">
                <div className="text-sm font-medium text-slate-900">{a.outcome}</div>
                <div className="text-xs text-slate-500 mt-0.5">Approver: <span className="font-semibold text-mlily-blue">{a.approver}</span> · {a.notes}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Do not promise products, parts or technician attendance before approval.</p>
        </Card>
      </div>

      <div className="flex flex-col items-center">
        <Arrow />
        <Node i={0} icon={ClipboardCheck} title="Warranty claim received" />
        <Arrow />
        <Node i={1} icon={Search} title="Check customer information" sub="Proof of purchase, product details, photos/videos, reported problem" />
        <Arrow />
        <Node i={2} title="Identify product / model" color="action" />
        <Arrow />
        <Node i={3} icon={Search} title="Check troubleshooting guide" color="action" />
        <Arrow />
        <Node i={4} icon={HelpCircle} title="Can the issue be safely troubleshooted?" color="mlily" />

        {/* Branch */}
        <div className="grid md:grid-cols-2 gap-6 w-full max-w-4xl mt-4">
          <Card className="p-5 border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-700 font-heading font-bold mb-3">
              <CheckCircle2 className="w-5 h-5" /> YES
            </div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span>1.</span> Follow approved troubleshooting instructions</li>
              <li className="flex gap-2"><span>2.</span> Send / use approved video where appropriate</li>
              <li className="flex gap-2"><span>3.</span> Record outcome</li>
            </ul>
          </Card>
          <Card className="p-5 border-red-200">
            <div className="flex items-center gap-2 text-red-700 font-heading font-bold mb-3">
              <AlertOctagon className="w-5 h-5" /> NO
            </div>
            <p className="text-sm text-slate-700">Escalate for review and approval.</p>
          </Card>
        </div>

        <Arrow />
        <div className="w-full max-w-4xl">
          <h3 className="text-center text-sm uppercase tracking-wider font-bold text-slate-400 mb-3">Possible outcomes</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["Issue resolved", "success"],
              ["Replacement part required", "action"],
              ["Technician / inspection required", "action"],
              ["Mattress / base replacement approval", "escalate"],
              ["Further internal review required", "escalate"],
            ].map(([t, c], i) => (
              <div key={t} className={cn("rounded-lg border-2 px-4 py-3 text-sm font-medium text-center",
                c === "success" ? "border-emerald-300 bg-emerald-50 text-emerald-700" :
                c === "action" ? "border-amber-300 bg-amber-50 text-amber-700" :
                "border-red-300 bg-red-50 text-red-700")}>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
